/** biome-ignore-all lint/correctness/noUnusedImports: reason */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { useFirebase } from "../Firebase";

const isDev = process.env.NODE_ENV === "development";

const getStoredCompanyLink = () => {
  if (typeof window === "undefined") return undefined;

  try {
    return localStorage.getItem("authCompLink") || undefined;
  } catch (error) {
    if (isDev) {
      console.warn("[Auth] Unable to read stored companyLink", error);
    }

    return undefined;
  }
};

const AuthContext = createContext();
const UserContext = createContext();

export const AuthProvider = ({ children }) => {
  const firebase = useFirebase();

  const [authState, setAuthState] = useState({
    claims: undefined,
    isLoading: true,
    user: undefined,
    userID: undefined,
  });

  const [userState, setUserState] = useState(undefined);

  const handleLoginRedirect = useCallback(async authUser => {
    if (!authUser && window.location.pathname !== "/") {
      if (isDev) {
        console.log(
          "[Auth] No auth user, redirecting to login from:",
          window.location.pathname
        );
      }

      window.location.replace("/");
    }
  }, []);

  const handleDashboardRedirect = useCallback(async claims => {
    if (claims && !claims.firstLogin && window.location.pathname === "/") {
      if (isDev) {
        console.log("[Auth] User authenticated, redirecting to dashboard");
      }

      window.location.replace("/dashboard");
    }
  }, []);

  useEffect(() => {
    if (isDev) {
      console.log("[Auth] Setting up auth state listener...");
    }

    firebase.checkUserAuth(async authUser => {
      if (isDev) {
        console.log("[Auth] checkUserAuth callback received:", {
          hasAuthUser: !!authUser,
          uid: authUser?.uid,
        });
      }

      handleLoginRedirect(authUser);
      if (authUser) {
        if (isDev) {
          console.log("[Auth] Fetching claims for user:", authUser.uid);
        }

        const claims = await firebase.claims();

        if (isDev) {
          console.log("[Auth] Claims received:", {
            companyLink: claims?.companyLink,
            hasClaims: !!claims,
            isAdmin: claims?.isAdmin,
            isClient: claims?.isClient,
          });
        }

        console.log(claims, "claims on auth");

        firebase.user(authUser.uid).update({
          lastLogin: new Date(),
        });

        const hasValidCompanyLink = !!claims?.companyLink;

        if (isDev && !hasValidCompanyLink) {
          console.warn(
            "[Auth] WARNING: claims.companyLink is missing or falsy!",
            {
              claims,
              willSetClaimsTo: hasValidCompanyLink ? claims : undefined,
            }
          );
        }

        setAuthState(prev => ({
          ...prev,
          claims: claims?.companyLink ? claims : undefined,
          isLoading: false,
          user: authUser,
          userId: authUser.uid,
        }));

        handleDashboardRedirect(claims);
      } else {
        if (isDev) {
          console.log("[Auth] No auth user, clearing auth state");
        }

        if (typeof window !== "undefined") {
          try {
            localStorage.removeItem("authCompLink");
          } catch (error) {
            if (isDev) {
              console.warn("[Auth] Unable to clear stored companyLink", error);
            }
          }
        }

        setAuthState(prev => ({
          ...prev,
          claims: undefined,
          isLoading: false,
          user: undefined,
          userID: undefined,
        }));
      }
    });
  }, [
    firebase,
    handleDashboardRedirect,
    handleLoginRedirect,
  ]);

  const handleUserSnapshot = useCallback(snap => {
    if (isDev) {
      console.log("[Auth] User snapshot received:", {
        hasData: snap.exists,
        userId: snap.id,
      });
    }

    setUserState(prev => ({
      ...prev,
      ...snap.data(),
      id: snap.id,
    }));
  }, []);

  useEffect(() => {
    if (isDev) {
      console.log(
        "[Auth] User state listener setup, uid:",
        authState?.user?.uid
      );
    }

    const unsubscribe = authState?.user?.uid
      ? firebase
          .user(authState.user.uid)
          .onSnapshot(handleUserSnapshot, error => {
            if (isDev) {
              console.error("[Auth] User snapshot error:", error);
            }
            console.log(error);
          })
      : () => {
          if (isDev) {
            console.log("[Auth] No user uid, skipping user snapshot listener");
          }
        };

    return () => unsubscribe();
  }, [
    authState,
    firebase,
    handleUserSnapshot,
  ]);

  useEffect(() => {
    if (authState === undefined || userState === undefined) return;

    if (authState.claims == null && userState?.companyLink) {
      if (isDev) {
        console.log("[Auth] Deriving claims from userState (fallback):", {
          companyLink: userState.companyLink,
          role: userState.role,
        });
      }

      const claims = {
        companyLink: userState.companyLink,
        isAdmin: userState.role === "admin",
        isClient: userState.role === "client",
        isDistributor: userState.role === "distributor",
        isWarehouse: userState.role === "warehouse",
      };

      setAuthState(prev => ({
        ...prev,
        claims,
      }));
    }
  }, [
    authState,
    userState,
  ]);

  const updateCompany = companyLink => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("authCompLink", companyLink);
      } catch (error) {
        if (isDev) {
          console.warn("[Auth] Unable to persist companyLink", error);
        }
      }
    }
    setAuthState(prev => ({
      ...prev,
      claims: {
        ...(prev.claims || {}),
        companyLink,
      },
    }));
  };

  const clearData = () => {
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("authCompLink");
      } catch (error) {
        if (isDev) {
          console.warn("[Auth] Unable to clear stored companyLink", error);
        }
      }
    }
    setAuthState(prev => ({
      ...prev,
      claims: undefined,
      user: undefined,
      userID: undefined,
    }));
  };

  const derivedCompanyLink =
    authState.claims?.companyLink
    || userState?.companyLink
    || getStoredCompanyLink();

  useEffect(() => {
    if (!derivedCompanyLink) return;
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem("authCompLink", derivedCompanyLink);
    } catch (error) {
      if (isDev) {
        console.warn("[Auth] Unable to persist derived companyLink", error);
      }
    }
  }, [
    derivedCompanyLink,
  ]);

  const claimsWithFallback = derivedCompanyLink
    ? {
        ...(authState.claims || {}),
        companyLink: derivedCompanyLink,
      }
    : authState.claims;

  return (
    <UserContext.Provider value={userState}>
      <AuthContext.Provider
        value={{
          ...authState,
          claims: claimsWithFallback,
          clearData,
          companyLink: derivedCompanyLink,
          updateCompany,
        }}
      >
        {children}
      </AuthContext.Provider>
    </UserContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export const useUser = () => useContext(UserContext);
